<script setup lang="ts">
import { ref } from 'vue';
import { useIntersectionObserver } from '@vueuse/core';

import {
  useBrowserStore,
  usePaxStore,
  usePriceCalendarStore,
} from '@apps/main/new-desktop/core/stores';
import { CoreEventSource } from '@apps/main/new-desktop/core/models';

import { AppButton } from '@libs/ui';
import { isFeatureToggleActive } from '@libs/feature-toggle';

import { PriceCalendar } from '../../../../modules/priceCalendar';
import {
  useTripOverviewPageStore,
  useTripOverviewPanelsStore,
  useTripSummaryStore,
} from '../../stores';

import ExperimentTripSummaryMobileLoader from './ExperimentTripSummaryMobileLoader.vue';
import ExperimentTripPriceSummaryFooter from './ExperimentTripPriceSummaryFooter.vue';
import TripPriceSummary from './TripPriceSummary.vue';

const tripSummaryStore = useTripSummaryStore();
const paxStore = usePaxStore();
const tripOverviewPanelsStore = useTripOverviewPanelsStore();
const tripOverviewPageStore = useTripOverviewPageStore();
const browserStore = useBrowserStore();
const priceCalendarStore = usePriceCalendarStore();

const tripSummaryRef = ref(null);

useIntersectionObserver(
  tripSummaryRef,
  (entries: IntersectionObserverEntry[]) => {
    if (entries[0]) {
      tripSummaryStore.isTripSummaryVisible = entries[0].isIntersecting;
    }
  }
);

function onSelectMonth(dayInMonth: Date): void {
  priceCalendarStore.selectItineraryMonth(
    dayInMonth,
    CoreEventSource.MonthsSelector
  );
}

function onSelectDate(date: Date): void {
  priceCalendarStore.selectItineraryDate(date, CoreEventSource.MonthsSelector);
}
</script>

<template>
  <div class="experiment-trip-summary-panel">
    <template v-if="browserStore.isLargerThanMediumMax">
      <PriceCalendar
        :selected-month="priceCalendarStore.selectedMonthOption.id"
        :months="priceCalendarStore.priceRibbonMonths"
        :price-calendar-state="priceCalendarStore.priceCalendarState"
        class="price-calendar"
        @select-date="onSelectDate"
        @select-month="onSelectMonth"
      >
        <template #headerSecondaryAction>
          <AppButton
            type="primary"
            state="enabled"
            size="small"
            :icon="{ name: 'share' }"
            class="share-button"
            @click="tripSummaryStore.openShareWidget"
          >
            {{ $t('planner.share_or_save_trip') }}
          </AppButton>
        </template>
      </PriceCalendar>

      <div class="summary-desktop">
        <ExperimentTripPriceSummaryFooter
          :paxes="paxStore.getPaxesButtonSimpleText"
          :travel-dates="tripSummaryStore.travelDateRange"
          :total-price-with-addons="tripSummaryStore.tripTotalPrice"
          :should-show-share-button="false"
          :with-border="true"
          @total-price-clicked="
            tripOverviewPanelsStore.openTripPriceSummaryPanel
          "
        />
      </div>
    </template>

    <template v-else>
      <template
        v-if="
          tripOverviewPageStore.anyItineraryLoaded &&
          !isFeatureToggleActive('loader')
        "
      >
        <div ref="tripSummaryRef">
          <TripPriceSummary
            class="trip-summary-price-summary"
            :price-segments="tripSummaryStore.tripPriceSegments"
          />

          <ExperimentTripPriceSummaryFooter
            :paxes="paxStore.getPaxesButtonSimpleText"
            :travel-dates="tripSummaryStore.travelDateRange"
            :total-price-with-addons="tripSummaryStore.tripTotalPrice"
            :should-show-share-button="true"
            :with-border="false"
          />
        </div>
      </template>
      <template v-else>
        <ExperimentTripSummaryMobileLoader />
      </template>
    </template>
  </div>
</template>

<style scoped lang="scss">
.experiment-trip-summary-panel {
  background-color: var(--color-white);
  height: 100vh;

  container-type: inline-size;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  .share-button {
    margin-right: var(--spacing-8);
  }

  .price-calendar {
    flex-grow: 1;
  }

  @include for-less-or-equal-than-medium-max {
    height: auto;
    border-radius: var(--border-radius-12);
  }

  @include for-less-or-equal-than-medium-max {
    border: 1px solid var(--color-secondary-93);
    padding: var(--spacing-16);
  }

  .summary-desktop {
    padding: 0 var(--spacing-24) var(--spacing-24) var(--spacing-24);
  }

  @container (max-width: 480px) {
    .summary-desktop {
      padding: 0 var(--spacing-12) var(--spacing-12) var(--spacing-12);
    }
  }
}
</style>
